/**
 * EditableBacklog Component
 * 
 * Main container for managing and editing a parsed backlog.
 * Allows users to view, edit, add, remove, and reorder stories.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { UserStory, ParsedBacklog, STANDARD_TASKS } from '../types/story';
import { StoryCard } from './StoryCard';
import { StoryEditor } from './StoryEditor';
import { Card } from './UIComponents';
import { getNextStoryId } from '../utils/storyIds';

interface EditableBacklogProps {
  backlog: ParsedBacklog;
  onBacklogChange: (backlog: ParsedBacklog) => void;
  onExportMarkdown: () => void;
  onExportExcel: () => void;
}

/**
 * Create an empty story template
 */
const createEmptyStory = (id: string): UserStory => ({
  id,
  title: '',
  user_story: '',
  rol: '',
  criterios_funcionales: [],
  criterios_tecnicos: [],
  figma_links: [],
  insumos: [],
  story_points: 3,
  tasks: [...STANDARD_TASKS],
  priority: 'Media',
  priority_sequence: null,
  status: 'draft'
});

export const EditableBacklog: React.FC<EditableBacklogProps> = ({
  backlog,
  onBacklogChange,
  onExportMarkdown,
  onExportExcel
}) => {
  const [expandedStoryId, setExpandedStoryId] = useState<string | null>(null);
  const [editingStory, setEditingStory] = useState<UserStory | null>(null);
  const [isNewStory, setIsNewStory] = useState(false);
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'priority' | 'sequence' | 'points' | 'status'>('priority');
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filter and sort stories
  const filteredStories = useMemo(() => {
    let stories = [...backlog.stories];

    // Apply filters
    if (filterPriority !== 'all') {
      stories = stories.filter(s => s.priority === filterPriority);
    }

    if (filterStatus !== 'all') {
      stories = stories.filter(s => s.status === filterStatus);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      stories = stories.filter(s => 
        s.title.toLowerCase().includes(term) ||
        s.user_story.toLowerCase().includes(term) ||
        s.rol.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    const priorityOrder = { Alta: 0, Media: 1, Baja: 2 };
    const statusOrder = { draft: 0, review: 1, approved: 2 };

    stories.sort((a, b) => {
      switch (sortBy) {
        case 'sequence': {
          const seqA = a.priority_sequence ?? Number.MAX_SAFE_INTEGER;
          const seqB = b.priority_sequence ?? Number.MAX_SAFE_INTEGER;
          if (seqA !== seqB) return seqA - seqB;
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        case 'priority':
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        case 'points':
          return b.story_points - a.story_points;
        case 'status':
          return statusOrder[a.status] - statusOrder[b.status];
        default:
          return 0;
      }
    });

    return stories;
  }, [backlog.stories, filterPriority, filterStatus, searchTerm, sortBy]);

  // Paginación calculada
  const paginationData = useMemo(() => {
    const totalItems = filteredStories.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedStories = filteredStories.slice(startIndex, endIndex);
    
    return {
      totalItems,
      totalPages,
      startIndex,
      endIndex: Math.min(endIndex, totalItems),
      paginatedStories,
      hasNext: currentPage < totalPages,
      hasPrev: currentPage > 1
    };
  }, [filteredStories, currentPage, itemsPerPage]);

  // Reset página cuando cambian filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filterPriority, filterStatus, searchTerm, sortBy]);

  // Statistics
  const stats = useMemo(() => {
    const total = backlog.stories.length;
    const totalPoints = backlog.stories.reduce((sum, s) => sum + s.story_points, 0);
    const byPriority = {
      Alta: backlog.stories.filter(s => s.priority === 'Alta').length,
      Media: backlog.stories.filter(s => s.priority === 'Media').length,
      Baja: backlog.stories.filter(s => s.priority === 'Baja').length,
    };
    const byStatus = {
      draft: backlog.stories.filter(s => s.status === 'draft').length,
      review: backlog.stories.filter(s => s.status === 'review').length,
      approved: backlog.stories.filter(s => s.status === 'approved').length,
    };
    return { total, totalPoints, byPriority, byStatus };
  }, [backlog.stories]);

  const handleToggleExpand = (storyId: string) => {
    setExpandedStoryId(prev => prev === storyId ? null : storyId);
  };

  const handleEditStory = (story: UserStory) => {
    setEditingStory(story);
    setIsNewStory(false);
  };

  const handleAddStory = () => {
    const newStory = createEmptyStory(getNextStoryId(backlog.stories));
    setEditingStory(newStory);
    setIsNewStory(true);
  };

  const handleSaveStory = (updatedStory: UserStory) => {
    let newStories: UserStory[];

    if (isNewStory) {
      newStories = [...backlog.stories, updatedStory];
    } else {
      newStories = backlog.stories.map(s => 
        s.id === updatedStory.id ? updatedStory : s
      );
    }

    onBacklogChange({
      ...backlog,
      stories: newStories
    });

    setEditingStory(null);
    setIsNewStory(false);
  };

  const handleDeleteStory = (storyId: string) => {
    if (window.confirm('¿Está seguro de eliminar esta historia de usuario?')) {
      onBacklogChange({
        ...backlog,
        stories: backlog.stories.filter(s => s.id !== storyId)
      });
    }
  };

  const handleDuplicateStory = (story: UserStory) => {
    const duplicated: UserStory = {
      ...story,
      id: getNextStoryId(backlog.stories),
      title: `${story.title} (Copia)`,
      status: 'draft'
    };

    onBacklogChange({
      ...backlog,
      stories: [...backlog.stories, duplicated]
    });
  };

  const handleCancelEdit = () => {
    setEditingStory(null);
    setIsNewStory(false);
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats and Actions */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Stats */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-center px-4 py-2 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
              <div className="text-xs text-gray-500">Historias</div>
            </div>
            <div className="text-center px-4 py-2 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-purple-600">{stats.totalPoints}</div>
              <div className="text-xs text-gray-500">Story Points</div>
            </div>
            <div className="flex gap-2">
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                {stats.byPriority.Alta} Alta
              </span>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                {stats.byPriority.Media} Media
              </span>
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                {stats.byPriority.Baja} Baja
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleAddStory}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
            >
              ➕ Nueva Historia
            </button>
            <button
              onClick={onExportMarkdown}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm"
            >
              📄 Exportar MD
            </button>
            <button
              onClick={onExportExcel}
              className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors font-medium text-sm"
            >
              📊 Exportar Excel
            </button>
          </div>
        </div>
      </Card>

      {/* Filters and Search */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="🔍 Buscar por título, descripción o rol..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Priority Filter */}
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todas las prioridades</option>
            <option value="Alta">🔴 Alta</option>
            <option value="Media">🟡 Media</option>
            <option value="Baja">🟢 Baja</option>
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos los estados</option>
            <option value="draft">📝 Borrador</option>
            <option value="review">👀 En Revisión</option>
            <option value="approved">✅ Aprobada</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="priority">Ordenar: Prioridad</option>
            <option value="sequence">Ordenar: Secuencia</option>
            <option value="points">Ordenar: Story Points</option>
            <option value="status">Ordenar: Estado</option>
          </select>
        </div>
      </Card>

      {/* Stories List with Pagination */}
      <div className="space-y-4">
        {paginationData.paginatedStories.length > 0 ? (
          <>
            {/* Pagination Header */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">
                Mostrando <span className="font-semibold">{paginationData.startIndex + 1}</span> - <span className="font-semibold">{paginationData.endIndex}</span> de <span className="font-semibold">{paginationData.totalItems}</span> historias
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Por página:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {/* Stories */}
            {paginationData.paginatedStories.map((story, index) => (
              <StoryCard
                key={story.id}
                story={story}
                index={paginationData.startIndex + index}
                isExpanded={expandedStoryId === story.id}
                onToggleExpand={() => handleToggleExpand(story.id)}
                onEdit={() => handleEditStory(story)}
                onDelete={() => handleDeleteStory(story.id)}
                onDuplicate={() => handleDuplicateStory(story)}
              />
            ))}

            {/* Pagination Controls */}
            {paginationData.totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={!paginationData.hasPrev}
                  className="px-3 py-1 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  ⏮️
                </button>
                <button
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={!paginationData.hasPrev}
                  className="px-3 py-1 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  ◀️ Anterior
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (paginationData.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= paginationData.totalPages - 2) {
                      pageNum = paginationData.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded text-sm ${
                          currentPage === pageNum
                            ? 'bg-gft-purple text-white'
                            : 'border hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!paginationData.hasNext}
                  className="px-3 py-1 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Siguiente ▶️
                </button>
                <button
                  onClick={() => setCurrentPage(paginationData.totalPages)}
                  disabled={!paginationData.hasNext}
                  className="px-3 py-1 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  ⏭️
                </button>
              </div>
            )}
          </>
        ) : (
          <Card className="text-center py-12">
            <span className="text-5xl mb-4 block">📋</span>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {searchTerm || filterPriority !== 'all' || filterStatus !== 'all' 
                ? 'No se encontraron historias'
                : 'No hay historias de usuario'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterPriority !== 'all' || filterStatus !== 'all'
                ? 'Intenta cambiar los filtros o el término de búsqueda'
                : 'Haz clic en "Nueva Historia" para agregar la primera'}
            </p>
            {!searchTerm && filterPriority === 'all' && filterStatus === 'all' && (
              <button
                onClick={handleAddStory}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                ➕ Crear Primera Historia
              </button>
            )}
          </Card>
        )}
      </div>

      {/* Edit Modal */}
      {editingStory && (
        <StoryEditor
          story={editingStory}
          onSave={handleSaveStory}
          onCancel={handleCancelEdit}
          isNew={isNewStory}
        />
      )}
    </div>
  );
};
